import { useRef, useState } from 'react';
import { ChevronRight, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import HelpDot from '@/components/ui/HelpDot';
import Spinner from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

/**
 * A read-only document authored upstream in Writers Room — the script or the
 * creative brief. Both are collapsible previews with an upload-a-new-version
 * affordance; neither is editable here, because Writers Room is the system of
 * record and an edit made here would be silently overwritten.
 */
export default function UploadableDocCard({
  title,
  help,
  meta,
  accept,
  uploadLabel,
  emptyText,
  onUpload,
  children,
  defaultOpen = false,
}: {
  title: string;
  help: string;
  /** Small right-of-title detail, e.g. "412 lines · 18.2 KB". */
  meta?: string;
  accept: string;
  uploadLabel: string;
  emptyText: string;
  onUpload: (file: File) => Promise<void>;
  children?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
      setOpen(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <button className="flex min-w-0 items-center gap-1.5 text-left text-sm font-medium" onClick={() => setOpen((o) => !o)}>
            <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-primary transition-transform', open && 'rotate-90')} />
            <span className="truncate">{title}</span>
            {meta && <span className="shrink-0 font-mono text-[10px] font-normal text-faint">{meta}</span>}
          </button>
          <div className="flex shrink-0 items-center gap-1.5">
            <HelpDot>{help}</HelpDot>
            <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={handleFile} />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? <Spinner /> : <Upload className="mr-1 h-3 w-3" />}
              {uploadLabel}
            </Button>
          </div>
        </div>
      </CardHeader>
      {open && <CardContent>{children ?? <p className="text-sm text-muted-foreground">{emptyText}</p>}</CardContent>}
    </Card>
  );
}
